import { useState } from 'react';
import { Users, Gift, Copy, Check, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function ReferralTab({ token, isActive, referralData, setReferralData, onGoToMembership }) {
  const [isLoadingReferral, setIsLoadingReferral] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);

  async function generateReferralCode() {
    try {
      setIsLoadingReferral(true);
      const response = await fetch(`${API}/referral/my-code`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setReferralData(prev => ({ ...prev, ...data, has_code: true, code: data.code }));
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsLoadingReferral(false);
    }
  }

  function copyReferralCode() {
    if (referralData?.code) {
      navigator.clipboard.writeText(referralData.code);
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000);
    }
  }

  if (!isActive) {
    return (
      <div className="text-center py-12">
        <Users className="w-12 h-12 mx-auto mb-4 text-gray-600" />
        <p className="text-gray-400 mb-4">Requieres membresía vigente para acceder al programa de referidos</p>
        <Button onClick={onGoToMembership} className="bg-amber-500 hover:bg-amber-400 text-black font-semibold">Activar Membresía</Button>
      </div>
    );
  }

  if (isLoadingReferral && !referralData) {
    return (
      <div className="text-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-amber-400 mx-auto" />
      </div>
    );
  }

  return (
    <div className="grid md:grid-cols-2 gap-8">
      <div className="space-y-6">
        <div className="p-6 bg-gradient-to-br from-amber-500/20 to-amber-600/10 border border-amber-500/30 rounded-xl">
          <h3 className="text-white font-semibold mb-2">Tu Código de Referido</h3>
          {referralData?.has_code ? (
            <div className="flex items-center gap-3 mt-4">
              <code className="flex-1 p-3 bg-[#111111] rounded-lg text-amber-400 font-mono text-xl text-center">{referralData.code}</code>
              <Button onClick={copyReferralCode} variant="outline" className="border-amber-500 text-amber-400 hover:bg-amber-500/20">
                {codeCopied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
              </Button>
            </div>
          ) : (
            <Button onClick={generateReferralCode} disabled={isLoadingReferral} className="bg-amber-500 hover:bg-amber-400 text-black font-semibold mt-4 w-full">
              {isLoadingReferral ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Gift className="w-4 h-4 mr-2" />}
              Generar Mi Código
            </Button>
          )}
        </div>
        <div className="p-4 bg-[#111111] rounded-lg">
          <h4 className="text-white font-medium mb-3">¿Cómo funciona?</h4>
          <ul className="space-y-2 text-sm text-gray-400">
            <li className="flex items-start gap-2"><span className="text-amber-400">1.</span>Comparte tu código con colegas</li>
            <li className="flex items-start gap-2"><span className="text-amber-400">2.</span>Ellos obtienen 10% de descuento</li>
            <li className="flex items-start gap-2"><span className="text-amber-400">3.</span>Tú recibes $174 MXN de crédito</li>
            <li className="flex items-start gap-2"><span className="text-amber-400">4.</span>Usa tu crédito en tu renovación</li>
          </ul>
        </div>
      </div>
      <div className="space-y-6">
        <ReferralStats referralData={referralData} />
        <ReferralHistory referralData={referralData} />
      </div>
    </div>
  );
}

function ReferralStats({ referralData }) {
  return (
    <div className="grid grid-cols-2 gap-4">
      <div className="p-4 bg-[#111111] rounded-lg text-center">
        <p className="text-3xl font-bold text-amber-400">{referralData?.total_referrals || 0}</p>
        <p className="text-gray-400 text-sm">Referidos</p>
      </div>
      <div className="p-4 bg-[#111111] rounded-lg text-center">
        <p className="text-3xl font-bold text-green-400">${(referralData?.available_credit || 0).toFixed(0)}</p>
        <p className="text-gray-400 text-sm">Crédito Disponible</p>
      </div>
    </div>
  );
}

function ReferralHistory({ referralData }) {
  const history = referralData?.referral_history || [];
  
  if (history.length === 0) {
    return null;
  }

  const items = [];
  for (let i = 0; i < history.length; i++) {
    const ref = history[i];
    items.push(
      <div key={i} className="flex justify-between items-center p-3 bg-[#111111] rounded-lg">
        <div>
          <p className="text-white text-sm">{ref.referred_name}</p>
          <p className="text-gray-500 text-xs">{new Date(ref.created_at).toLocaleDateString('es-MX')}</p>
        </div>
        <Badge className="bg-green-500/20 text-green-400">+${ref.reward_earned?.toFixed(0) || 0}</Badge>
      </div>
    );
  }

  return (
    <div>
      <h4 className="text-white font-medium mb-3">Historial de Referidos</h4>
      <div className="space-y-2 max-h-48 overflow-y-auto">
        {items}
      </div>
    </div>
  );
}
